import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { configureRevenueCat, getRevenueCatInstance, REVENUECAT_PRO_ENTITLEMENT } from '../config/revenuecat';
import { findRevenueCatPackage } from '../utils/revenueCatProducts';

export const RevenueCatContext = createContext(null);

export const RevenueCatProvider = ({ children }) => {
    const { userData } = useSelector(state => state.user);
    const [customerInfo, setCustomerInfo] = useState(null);
    const [loadingCustomerInfo, setLoadingCustomerInfo] = useState(false);

    const refreshCustomerInfo = useCallback(async () => {
        const purchases = getRevenueCatInstance();
        if (!purchases) return null;

        try {
            setLoadingCustomerInfo(true);
            const info = await purchases.getCustomerInfo();
            setCustomerInfo(info);
            return info;
        } catch (error) {
            console.log('unable to fetch revenuecat customer info; ', error.message);
            return null;
        } finally {
            setLoadingCustomerInfo(false);
        }
    }, []);

    useEffect(() => {
        if (!userData?.userId) return;

        let cancelled = false;

        const init = async () => {
            try {
                await configureRevenueCat(userData.userId);
                if (!cancelled) await refreshCustomerInfo();
            } catch (error) {
                console.log('unable to configure revenuecat; ', error.message);
            }
        };

        init();

        return () => { cancelled = true };
    }, [userData?.userId, refreshCustomerInfo]);

    const getOfferings = useCallback(async (params) => {
        const purchases = getRevenueCatInstance();
        if (!purchases) throw new Error('RevenueCat is not ready yet. Please try again in a moment.');
        return purchases.getOfferings(params);
    }, []);

    const purchasePackage = useCallback(async (rcPackage) => {
        const purchases = getRevenueCatInstance();
        if (!purchases) throw new Error('RevenueCat is not ready yet. Please try again in a moment.');

        const result = await purchases.purchase({ rcPackage });
        setCustomerInfo(result.customerInfo);
        return result;
    }, []);

    // Resolves a coin package fetched from GET /api/payment/coin-packages to
    // its RevenueCat product (see utils/revenueCatProducts) and purchases it.
    // Searches every configured offering (not just "current") since coin
    // packages and the Pro subscription are likely set up as separate offerings.
    const purchaseCoinPackage = useCallback(async (coinPackage) => {
        const offerings = await getOfferings();
        const offeringsToSearch = [offerings.current, ...Object.values(offerings.all)].filter(Boolean);

        let rcPackage = null;
        for (const offering of offeringsToSearch) {
            rcPackage = findRevenueCatPackage(offering, coinPackage);
            if (rcPackage) break;
        }

        if (!rcPackage) {
            throw new Error(`"${coinPackage.name}" isn't set up for purchase yet. Please contact support.`);
        }

        return purchasePackage(rcPackage);
    }, [getOfferings, purchasePackage]);

    // Web Billing has no bundled Customer Center UI (that's mobile-only for
    // now), so the paywall's "visit customer center" action opens the
    // subscription management URL RevenueCat already tracks on CustomerInfo.
    const presentProPaywall = useCallback(async (options = {}) => {
        const purchases = getRevenueCatInstance();
        if (!purchases) throw new Error('RevenueCat is not ready yet. Please try again in a moment.');

        const result = await purchases.presentPaywall({
            ...options,
            onVisitCustomerCenter: () => {
                if (customerInfo?.managementURL) {
                    window.open(customerInfo.managementURL, '_blank', 'noopener,noreferrer');
                }
            },
        });
        setCustomerInfo(result.customerInfo);
        return result;
    }, [customerInfo]);

    const isPro = !!customerInfo?.entitlements?.active?.[REVENUECAT_PRO_ENTITLEMENT];

    const value = useMemo(() => ({
        customerInfo,
        loadingCustomerInfo,
        isPro,
        refreshCustomerInfo,
        getOfferings,
        purchasePackage,
        purchaseCoinPackage,
        presentProPaywall,
    }), [customerInfo, loadingCustomerInfo, isPro, refreshCustomerInfo, getOfferings, purchasePackage, purchaseCoinPackage, presentProPaywall]);

    return (
        <RevenueCatContext.Provider value={value}>
            {children}
        </RevenueCatContext.Provider>
    );
};
